
import _ from "lodash";
import rest from "@/rest";
import router from "@/router";
import { Component, Vue, Prop } from "vue-property-decorator";
import { vuex } from "@/store";
import { namespace } from "vuex-class";
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
// Components
import FileDrop from '@/components/Shared/FileDrop.vue';

// interfaces
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { CustomizationSelectionsVm } from "@/interfaces/CustomizationSelectionsVm";
import NumberInput from "@/components/Shared/NumberInput.vue";
import ExpandableCard from "@/components/Shared/ExpandableCard.vue";
import { CustomAttributeName } from "@/interfaces/CustomAttributeName";

const auth = namespace("auth");
const customization = namespace("customization");

@Component({
  components: {
    FileDrop,
    NumberInput,
    ExpandableCard
  }
})
export default class CustomerSettingsView extends Vue {
  @auth.Getter isAdmin: any;
  // @customization.Action loadCustomizationSettings!: any;
  @customization.Action saveCustomizationSettings!: any;
  @customization.Getter customizationSettings!: CustomizationVm;

  busy = false;
  customizationSelections: CustomizationSelectionsVm | null = null;
  editSettings: CustomizationVm | null = null;
  customAttributeNames: CustomAttributeName[] = [];

  // $refs!: {
  // }

  async mounted() {
    if (!this.isAdmin)
      router.push("/");

    // console.log("App name", this.customizationSettings?.appName);
    await this.loadCustomizationSelections();
    await this.loadCustomAttributeNames();
    this.editSettings = _.cloneDeep(this.customizationSettings);
  }

  created() {
    // Disable body scroll on iOS
    this.$nextTick(async () => {
      await this.$globalHelper.delay(10);
      const modal = document.querySelector('.modal');
      disableBodyScroll(modal);
    });
  }

  beforeDestroy() {
    clearAllBodyScrollLocks();
  }

  async loadCustomizationSelections() {
    try {
      await rest.url("admin/loadCustomizationSelections")
        .get()
        .then((response) => {
          this.customizationSelections = response;
        });
    } catch (error) {
      console.error("Error loading customization selections:", error);
    }
  }

  async loadCustomAttributeNames() {
    await rest.url("groupAdmin/loadCustomAttributeNames")
      .get()
      .then((response : CustomAttributeName[]) => {
        response.forEach((ca: CustomAttributeName, index: number) => {
          ca.id = index + 1;
        });
        this.customAttributeNames = response;
      })
  }

  async updateCustomAttributeNames() {
    await rest.url("groupAdmin/updateCustomAttributeNames")
      .post(this.customAttributeNames)
      .then((response:  boolean) =>  {
        if (response) {
          vuex.ux.SB_SUCCESS({
            message: "Kundenspezifische Attribute wurden aktualisiert.",
            timeout: 1500
          });
        }
      })
  }

  addCustomAttribute() {
    if (this.customAttributeNames.length >= 7) {
      vuex.ux.SB_FAILURE({
        message: "Es können maximal 7 kundenspezifische Attribute hinzugefügt werden.",
        timeout: 2000
      });
      return;
    }
    // const newId = this.customAttributeNames.length > 0
    //   ? Math.max(...this.customAttributeNames.map(attr => attr.id)) + 1
    //   : 1;
    const newIndex = this.customAttributeNames.length + 1;
    const attributeName = `customAttribute${newIndex.toString().padStart(2, '0')}`;
    this.customAttributeNames.push({ id: newIndex, attributeName: attributeName, customName: "" });
  }

  get chatModels() {
    if (!this.customizationSelections || !this.customizationSelections.chatModels)
      return [];
    // Filter chat models based on the selected chat service
    return this.customizationSelections.chatModels.filter(x => x.service == this.editSettings?.chatService) || [];
  }

  get multiModalLlmModels() {
    if (!this.customizationSelections || !this.customizationSelections.multiModalLlmModels)
      return [];
    // Filter multi-modal LLM models based on the selected multi-modal LLM service
    return this.customizationSelections.multiModalLlmModels.filter(x => x.service == this.editSettings?.multiModalLlmService) || [];
  }

  get ttsStandardModels() {
    if (!this.customizationSelections || !this.customizationSelections.ttsModels)
      return [];

    return this.customizationSelections.ttsModels.filter(x => x.service == this.editSettings?.standardTtsService) || [];
  }

  get ttsPodcastModels() {
    if (!this.customizationSelections || !this.customizationSelections.ttsModels)
      return [];

    return this.customizationSelections.ttsModels.filter(x => x.service == this.editSettings?.podcastTtsService) || [];
  }

  get transcriptionModels() {
    if (!this.customizationSelections || !this.customizationSelections.transcriptionModels)
      return [];
    // Filter transcription models based on the selected transcription service
    return this.customizationSelections.transcriptionModels.filter(x => x.service == this.editSettings?.transcriptionService) || [];
  }

  get imageModels() {
    if (!this.customizationSelections || !this.customizationSelections.imageModels)
      return [];
    // Filter image models based on the selected image generation service
    return this.customizationSelections.imageModels.filter(x => x.service == this.editSettings?.imageGenerationService) || [];
  }

  get videoModels() {
    if (!this.customizationSelections || !this.customizationSelections.videoModels)
      return [];
    // Filter video models based on the selected video generation service
    return this.customizationSelections.videoModels.filter(x => x.service == this.editSettings?.videoGenerationService) || [];
  }

  onSave() {
    this.busy = true;
    try {
      this.saveCustomizationSettings(this.editSettings);
    } finally {
      this.busy = false;
    }
  }

  get containerPadding() {
    if (this.$vuetify.breakpoint.xs)
      return "padding: 0px;";
    return "";
  }

  get lightTransparentColor() {
      return this.$store.state.ux.colorAccent5 + "40";
  }

  goHome() {
    if (router.currentRoute.path == "/")
      return;
    router.push("/");
  }
}
